import { useTranslation, Translation } from 'react-i18next';
import React, { memo, useMemo, useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';
import cx from 'clsx';
import { Virtuoso } from 'react-virtuoso';

import { Modal, UnionAvatar, FollowButton, HelperTooltip, UserCardPopup } from '@/components';
import {
  useArticleReadersList,
  useRepostersList,
  useArticleTrustersList,
  useArticleDistrustersList,
  UnionAccountWithRank,
  useArticlePayUserList,
  useArticlePayQaWathcersList,
  changeArticleUsersFollowing,
} from '../../hooks';
import { transAuthor } from '../../utils';
import { isOfficalAddress, openNewWindow, useCurrentUserId } from '@/utils';
import { IconRcFeedRank } from '@/assets/stream';
import style from './index.module.scss';

const SkeletonList = memo(({ count = 5 }) => (
  <>
    {Array.from({ length: count }).map((x, i) => (
      <div className={style.item} key={i}>
        <div className={style.leftContent}>
          <Skeleton circle height={48} width={48} className={style.avatar} />
          <div className={style.operationUserMain}>
            <Skeleton width={80} height={18} className={style.commentHead} />
            <Skeleton width={40} height={18} className={style.commentHead} />
          </div>
        </div>
        <Skeleton className={style.followBtn} />
      </div>
    ))}
  </>
));

const OperationUserListQueryMap = {
  repost: {
    query: useRepostersList,
    title: <Translation>{t => t('OperationUserListModal.rb9esk')}</Translation>,
  },
  trust: {
    query: useArticleTrustersList,
    title: <Translation>{t => t('OperationUserListModal.ffbadc')}</Translation>,
  },
  distrust: {
    query: useArticleDistrustersList,
    title: <Translation>{t => t('OperationUserListModal.albvv2')}</Translation>,
  },
  read: {
    query: useArticleReadersList,
    title: <Translation>{t => t('OperationUserListModal.19easxt')}</Translation>,
  },
};

export type StreamOperationType = keyof typeof OperationUserListQueryMap;

export const OperationUserList = memo(
  ({
    className,
    articleId,
    typeKey,
  }: {
    articleId: number;
    className?: string;
    typeKey: StreamOperationType;
  }) => {
    const { t } = useTranslation();

    const { query, title } = OperationUserListQueryMap[typeKey];
    const { data: users, isLoading } = query(articleId);

    return (
      <div className={style.userList}>
        <div className={style.header}>
          {title}
          {users?.length && users.length >= 100 ? (
            <span className={style.headerTip}>{t('OperationUserListModal.kkivjd')}</span>
          ) : null}
        </div>
        {isLoading ? (
          <div className={style.list}>
            <SkeletonList />
          </div>
        ) : users?.length ? (
          <div className={cx(style.list, className)}>
            {users.map((item: UnionAccountWithRank) => (
              <OperationUserItem user={item} key={item.id} />
            ))}
          </div>
        ) : null}
      </div>
    );
  },
);

const { CommonModal } = Modal;

const OperationUserItem = ({
  className,
  user,
  onFollowChange,
}: {
  className?: string;
  user: UnionAccountWithRank;
  onFollowChange?: (id: string, isFollowing: boolean) => void;
}) => {
  const { t } = useTranslation();

  const address = useCurrentUserId();
  const userId = user.id;
  const isOfficial = isOfficalAddress(userId);

  const handleUserClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      openNewWindow(
        isOfficial
          ? `/official/${(user as OfficialAccount)?.slug}/stream`
          : `/profile/${userId}/stream`,
        '_blank',
      );
    },
    [isOfficial, user, userId],
  );

  const name = useMemo(() => user._author || transAuthor({ user }), [user]);

  const handleFollowChange = useCallback(
    (isFollowing: boolean) => {
      onFollowChange?.(user.id, isFollowing);
    },
    [onFollowChange, user.id],
  );

  return (
    <div className={cx(style.item, className)} key={userId}>
      <div className={style.leftContent}>
        <div onClick={handleUserClick}>
          <UnionAvatar
            className={style.avatar}
            size={48}
            data={user}
            userCardPopup={{
              followCallback: handleFollowChange,
            }}
          />
        </div>
        <div className={style.operationUserMain}>
          <div className={style.commentHead}>
            <UserCardPopup
              address={isOfficalAddress(user.id) ? '' : user.id || ''}
              followCallback={handleFollowChange}>
              <div onClick={handleUserClick} className={style.uid}>
                {name}
              </div>
            </UserCardPopup>
          </div>
          <HelperTooltip title={t('OperationUserListModal.1h0gg1w')}>
            <div className={style.content}>
              <IconRcFeedRank /> <span>{user.rank_at}</span>
            </div>
          </HelperTooltip>
        </div>
      </div>

      <div>
        {address === userId || isOfficial ? null : (
          <FollowButton
            address={user.id}
            userInfo={user as UserInfo}
            className={style.followBtn}
            operateCallback={handleFollowChange}
          />
        )}
      </div>
    </div>
  );
};

export const OperationUserListModal = ({
  turnOff,
  articleId,
  modalType,
}: {
  articleId: number;
  on?: boolean;
  turnOff?: () => void;
  modalType: StreamOperationType;
}) => {
  const turnOffModal = useCallback(() => {
    turnOff && turnOff();
  }, [turnOff]);

  console.log('modalType', modalType);

  return (
    <CommonModal containerClassName={style.userListModal} size={'large'} turnOff={turnOffModal}>
      <OperationUserList articleId={articleId} typeKey={modalType} />
    </CommonModal>
  );
};

export const StreamUserListModal = ({
  on,
  turnOff,
  title,
  users,
  isLoading,
}: {
  title: string;
  on?: boolean;
  turnOff?: () => void;
  users?: UnionAccountWithRank[];
  isLoading?: boolean;
}) => {
  const turnOffModal = useCallback(() => {
    turnOff && turnOff();
  }, [turnOff]);

  return (
    <CommonModal
      on={on}
      containerClassName={style.userListModal}
      size={'large'}
      turnOff={turnOffModal}>
      <div className={style.userList}>
        <div className={style.header}>{title}</div>
        {isLoading ? (
          <SkeletonList />
        ) : users?.length ? (
          <div className={style.list}>
            {users.map(item => (
              <OperationUserItem key={item.id} user={item} />
            ))}
          </div>
        ) : null}
      </div>
    </CommonModal>
  );
};

const InfiniteUserListQueryMap = {
  paid: {
    query: useArticlePayUserList,
    title: <Translation>{t => t('OperationUserListModal.2273kk')}</Translation>,
  },
  payQa: {
    query: useArticlePayQaWathcersList,
    title: <Translation>{t => t('OperationUserListModal.2273kk')}</Translation>,
  },
};

export const StreamInfiniteUsersModal = ({
  on,
  turnOff,
  title,
  type,
  articleId,
}: {
  title: string;
  on?: boolean;
  turnOff?: () => void;
  type: keyof typeof InfiniteUserListQueryMap;
  articleId: number;
}) => {
  const address = useCurrentUserId();

  const { query, title: _title } = InfiniteUserListQueryMap[type];
  const { result, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = query(articleId);

  const fetchMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleFollowChange = useCallback(
    userId => {
      changeArticleUsersFollowing(articleId, userId, address);
    },
    [articleId, address],
  );

  const turnOffModal = useCallback(() => {
    turnOff && turnOff();
  }, [turnOff]);

  return (
    <CommonModal containerClassName={style.userListModal} size={'large'} turnOff={turnOffModal}>
      <div className={style.userList}>
        <div className={style.header}>{title || _title}</div>
        {isLoading ? (
          <div className={style.list}>
            <SkeletonList />
          </div>
        ) : result?.users?.length ? (
          <div className={style.list}>
            <Virtuoso
              style={{ height: '400px', marginRight: -24 }}
              data={result?.users}
              overscan={20}
              endReached={fetchMore}
              itemContent={(index, item) => (
                <OperationUserItem
                  user={item}
                  key={`${item.id}_${index}`}
                  onFollowChange={handleFollowChange}
                />
              )}
            />
          </div>
        ) : null}
      </div>
    </CommonModal>
  );
};

export const StreamUsersModal = ({
  on,
  turnOff,
  title,
  hook,
  params,
}: {
  title: string;
  on?: boolean;
  turnOff?: () => void;
  hook: (...arg: any) => any;
  params: Array<any>;
}) => {
  const turnOffModal = useCallback(() => {
    turnOff && turnOff();
  }, [turnOff]);

  const { result, isLoading } = hook.apply(null, params);

  return (
    <CommonModal
      on={on}
      containerClassName={style.userListModal}
      size={'large'}
      turnOff={turnOffModal}>
      <div className={style.userList}>
        <div className={style.header}>
          {title}{' '}
          {/* {result?.list?.length >= 100 ? (
            <span className={style.headerTip}>(latest 100)</span>
          ) : null} */}
        </div>
        {isLoading ? (
          <SkeletonList />
        ) : result?.list?.length ? (
          <div className={style.list}>
            {result?.list.map((item: any) => <OperationUserItem user={item} key={item.id} />)}
          </div>
        ) : null}
      </div>
    </CommonModal>
  );
};
